import { memo, useEffect, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import Cookies from "universal-cookie";
import Icon from "components/Elements/Icon/Icon.tsx";
import Image from "components/Elements/Image/Image.tsx";
import useBlackFridayUser from "hooks/useBlackFridayUser.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { ButtonA } from "components/Elements/Button/Button.js";
import useMediaQueries from "hooks/useMediaQueries.js";
import setBlackFridayContentMarginTop from "helpers/setBlackFridayContentMarginTop.ts";

import "./blackFriday.scss";

const cookies = new Cookies();

const LINK_TO_BLACK_FRIDAY_PROMO_PAGE =
  "https://campaign.jumbomail.me/%lang%?utm_source=site&utm_medium=email&utm_campaign=blackfriday";

const Logo = () => {
  const { getHtmlByKey, getTextByKey, getDirection, getLanguage } =
    useContent();
  const { tabletCondition, mobileCondition } = useMediaQueries();

  const linkWithLang = LINK_TO_BLACK_FRIDAY_PROMO_PAGE.replace(
    "%lang%",
    getLanguage() === "he" ? "he" : ""
  );

  return (
    <div className="promo-logo">
      <Image name="black-friday/bf_logo_sale" />
      {!mobileCondition && getDirection() === "rtl" && (
        <ButtonA href={linkWithLang} title={getTextByKey("get-it-now")} />
      )}
      {!tabletCondition && <p>{getHtmlByKey("bf-header-title")}</p>}
      {!mobileCondition && getDirection() === "ltr" && (
        <ButtonA href={linkWithLang} title={getTextByKey("get-it-now")} />
      )}
    </div>
  );
};

const CountNumberWithAnimation = memo(({ item, i }) => {
  return (
    <TransitionGroup component={null}>
      <CSSTransition
        key={item + i}
        timeout={1000}
        classNames="counter-animation"
        appear
        exit
      >
        <div className="promo-counter-time-item-value" key={item + i}>
          <span>{item}</span>
          <span className="prev">{item == 9 ? 0 : +item + 1}</span>
        </div>
      </CSSTransition>
    </TransitionGroup>
  );
});

export const Counter = () => {
  const { getTextByKey, getBlackFridayTime } = useContent();
  const time = getBlackFridayTime();
  const [timeState, setTimeState] = useState(time);
  const [days, setDays] = useState("");
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [seconds, setSeconds] = useState("");

  // transform minutes time to days hours minutes, if its less than 10 add 0 in front
  const transformTime = (time) => {
    const daysCalc = Math.floor(time / (60 * 24 * 60));
    const hoursCalc = Math.floor((time / 3600) % 24);
    const minutesCalc = Math.floor((time / 60) % 60);
    const secondsCalc = Math.floor(time % 60);
    const daysString = daysCalc < 10 ? `0${daysCalc}` : daysCalc + "";
    const hoursString = hoursCalc < 10 ? `0${hoursCalc}` : hoursCalc + "";
    const minutesString =
      minutesCalc < 10 ? `0${minutesCalc}` : minutesCalc + "";
    const secondsString =
      secondsCalc < 10 ? `0${secondsCalc}` : secondsCalc + "";
    setDays(daysString);
    setHours(hoursString);
    setMinutes(minutesString);
    setSeconds(secondsString);
  };

  useEffect(() => {
    if (time) {
      // set timer to do counter each second
      const timer = setInterval(() => {
        setTimeState((prev) => {
          if (prev <= 0) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [time]);

  useEffect(() => {
    transformTime(timeState);
  }, [timeState]);

  return (
    <div className="promo-counter">
      <div className="promo-counter-time-item">
        {days.split("").map((item, i) => (
          <CountNumberWithAnimation item={item} i={i} key={item + i} />
        ))}
        <div className="promo-counter-time-item-label">{getTextByKey("D")}</div>
      </div>
      <div className="promo-counter-time-item">
        {hours.split("").map((item, i) => (
          <CountNumberWithAnimation item={item} i={i} key={item + i} />
        ))}
        <div className="promo-counter-time-item-label">{getTextByKey("H")}</div>
      </div>
      <div className="promo-counter-time-item">
        {minutes.split("").map((item, i) => (
          <CountNumberWithAnimation item={item} i={i} key={item + i} />
        ))}
        <div className="promo-counter-time-item-label">{getTextByKey("M")}</div>
      </div>
    </div>
  );
};

const HomePagePromo = ({ discount = 30 }) => {
  return (
    <div className="logo-section">
      <Icon name="logo-jumbomail" orange />
      {discount}% OFF
      <Image name="black-friday/alabel_no_shadow" />
    </div>
  );
};

const BlackFridayHeader = () => {
  const {
    setBlackFridayTimeCookie,
    getBlackFridayTimeCookie,
    getIsBlackFriday,
    getTextByKey,
    getLanguage,
  } = useContent();
  const { mobileCondition } = useMediaQueries();
  // BLACK FRIDAY PROMO
  const time = useBlackFridayUser();
  // BLACK FRIDAY PROMO

  // set cookie to close modal
  const handleClose = (e) => {
    e.stopPropagation();
    cookies.set("BlackFridayModal", new Date().toLocaleDateString());
    setBlackFridayTimeCookie(false);
  };

  const linkWithLang = LINK_TO_BLACK_FRIDAY_PROMO_PAGE.replace(
    "%lang%",
    getLanguage() === "he" ? "he" : ""
  );

  const handleRedirectToPlans = () => {
    window.location.href = linkWithLang;
  };

  useEffect(() => {
    // cos of position fixed of header need to change some styles
    const elem = document.querySelector(".page-content");

    if (!getBlackFridayTimeCookie()) {
      elem.classList.remove("black-friday-header-exist");
    }

    if (time && getBlackFridayTimeCookie()) {
      elem.classList.add("black-friday-header-exist");
      const clearMargin = setBlackFridayContentMarginTop();
      
      return () => {
        elem.classList.remove("black-friday-header-exist");
        clearMargin();
      };
    }
  }, [time, getBlackFridayTimeCookie()]);

  return time && getBlackFridayTimeCookie() ? (
    <div className={`black-friday-header`} onClick={handleRedirectToPlans}>
      <div className="black-friday-promo">
        {!getIsBlackFriday() ? <HomePagePromo /> : <Logo />}
        <Counter />
        {mobileCondition && (
          <ButtonA
            href={linkWithLang}
            title={getTextByKey("get-it-now")}
            className="black-friday-action-button"
          />
        )}
      </div>
      <div className="black-friday-promo-close" onClick={handleClose}>
        <Icon name="x" white />
      </div>
    </div>
  ) : null;
};
export default BlackFridayHeader;
